import * as React from 'react'
import { useFormikContext } from 'formik'

import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import DialogActions from '@mui/material/DialogActions'
import Stack from '@mui/material/Stack'

import type {
  MissingFieldGroup,
  UpdateRequiredFieldsFormModel,
} from './typings'
import { AdditionalLeadField } from './fields/AdditionalLeadField'
import { AssignmentFields } from './fields/AssignmentFields'
import { AutoReviewCustomField } from './fields/AutoReviewCustomField'
import { BranchField } from './fields/BranchField'
import { NameOrCompanyFields } from './fields/NameOrCompanyFields'
import { PhoneOrEmailFields } from './fields/PhoneOrEmailFields'
import { UseReviewNote } from './fields/UseReviewNote'
import type { UseData } from './hooks/useData'
import DialogContent from '@mui/material/DialogContent'

type Props = {
  data: UseData
  onClose: () => void
}

function renderGroup(group: MissingFieldGroup) {
  switch (group.kind) {
    case 'nameOrCompany':
      return <NameOrCompanyFields key="nameOrCompany" />
    case 'phoneOrEmail':
      return <PhoneOrEmailFields key="phoneOrEmail" />
    case 'branchId':
      return <BranchField key="branchId" />
    case 'assignment':
      return <AssignmentFields key="assignment" fields={group.fields} />
    case 'additional':
      return group.key === 'referral' ? null : (
        <AdditionalLeadField key={group.key} fieldKey={group.key} />
      )
    case 'custom':
      return (
        <AutoReviewCustomField
          key={`custom-${group.fieldId}`}
          fieldId={group.fieldId}
        />
      )
    default:
      return null
  }
}

export function UpdateRequiredFieldsForm(props: Props) {
  const { data, onClose } = props
  const { loading, groups, nonEditableMissing } = data

  const { isSubmitting, submitForm } =
    useFormikContext<UpdateRequiredFieldsFormModel>()

  return (
    <>
      <DialogContent sx={{ px: '24px', pt: 0 }}>
        {loading ? (
          <CircularProgress />
        ) : (
          <Stack spacing={2} sx={{ pt: 2 }}>
            {groups.map(renderGroup)}
            {nonEditableMissing.map((field) => (
              <UseReviewNote
                key={`${field.fieldKey}-${field.fieldId}`}
                subject={
                  field.fieldKey === 'referral'
                    ? 'the referral'
                    : `"${field.fieldName}"`
                }
              />
            ))}
          </Stack>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          borderTop: '1px solid #e2e5ea',
          px: '24px',
          py: '16px',
        }}
      >
        <Button
          size="large"
          onClick={onClose}
          data-test-id="update-required-fields-cancel"
        >
          Cancel
        </Button>
        <Button
          size="large"
          variant="contained"
          disabled={isSubmitting}
          onClick={submitForm}
          data-test-id="update-required-fields-submit"
        >
          Save &amp; Auto-Review
        </Button>
      </DialogActions>
    </>
  )
}

UpdateRequiredFieldsForm.displayName = 'UpdateRequiredFieldsForm'
